import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Subject } from "rxjs";
import { useAuth } from "../../firebase/AuthProvider";
import { useChatConnection } from "../../contexts/ChatConnectionContext";
import {
    Message,
    getMessagesForConversation,
    sendMessage as sendMessageApi,
} from "../../services/chatService/ChatService";
import { toast } from "react-toastify";
import { getUTCTime } from "../../utilities/dateUtils";
import { ValidateOtpResponseDto } from "../../interfaces/ExtermalUserDtos/ExternalUsersDtos";
import { GetHeaders } from "../../services/videoService/VideoService";
import { isNewMessageReceivedSubject } from "../../services/Subjects/ChatSubjects";
import { CHAT_PAGE_SIZE } from "../../Data/SD";
import UserInfo from "../../interfaces/UserInfo";
import {
    ChatDeliveryStatus,
    PendingChatOutboxItem,
    createClientMessageId,
    createOptimisticTempId,
    deleteOutboxItem,
    getConversationOutbox,
    saveOutboxItem,
    updateOutboxItem,
} from "../../services/chatService/ChatOutbox";

export const conversationRead$ = new Subject<number>();

export interface ChatMessagePreviewEvent {
    conversationId: number;
    message: Message;
}

// Meeting UI can subscribe to this lightweight stream without creating another
// SignalR connection or another copy of the chat hook. The event is emitted from
// the same ReceiveMessage handler that already owns chat message delivery.
export const chatMessagePreview$ = new Subject<ChatMessagePreviewEvent>();

export interface OptimisticMessage extends Message {
    isOptimistic?: boolean;
    deliveryStatus?: ChatDeliveryStatus;
    clientOrder?: number;
    optimisticAttachmentName?: string;
}

export const useGlobalChat = (
    conversationId: number,
    externalUserDetails?: ValidateOtpResponseDto | null,
    isViewActive = true,
) => {
    const { currentUser } = useAuth();
    const { connection, connectionStatus, connectionVersion, resetConnection } = useChatConnection();

    const [messages, setMessages] = useState<OptimisticMessage[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [typingUsers, setTypingUsers] = useState<string[]>([]);

    const [hasMore, setHasMore] = useState<boolean>(true);
    const [loadingMore, setLoadingMore] = useState<boolean>(false);
    const earliestMessageIdRef = useMemo(() => ({ id: null as number | null }), []);
    const typingStateRef = useMemo(() => ({ isTyping: false }), []);
    const isViewActiveRef = useRef(isViewActive);
    const sendQueuesRef = useRef(new Map<number, Promise<void>>());
    const pendingSendsRef = useRef(new Map<number, PendingChatOutboxItem>());
    const activeDeliveriesRef = useRef(new Set<string>());
    const scheduledDeliveriesRef = useRef(new Set<string>());
    const reactionMutationVersionsRef = useRef(new Map<string, number>());

    useEffect(() => {
        isViewActiveRef.current = isViewActive;
    }, [isViewActive]);

    const receiveSound = useMemo(() => new Audio("/sounds/message-received.mp3"), []);

    // Standardized ID for the current user (Internal or External)
    const currentMyId = currentUser?.uid || externalUserDetails?.externalUserDetailId;

    useEffect(() => {
        if (externalUserDetails) {
            resetConnection(externalUserDetails);
        }
    }, [externalUserDetails, resetConnection]);

    useEffect(() => {
        setTypingUsers([]);
        typingStateRef.isTyping = false;
    }, [
    conversationId,
    typingStateRef,
    ]);
    // Join only the conversation currently open in the chat UI. Global message
    // notifications are delivered through Clients.User on the backend, so the
    // user does not need to join every conversation group.
    useEffect(() => {
        if (
            !connection ||
            connectionStatus !== "connected" ||
            !conversationId
        ) {
            return;
        }

        let joined = false;
        let disposed = false;

        const joinActiveConversation = async () => {
            try {
                await connection.invoke("JoinConversation", conversationId);

                if (disposed) {
                    // The user switched conversations while the join was in flight.
                    await connection.send("LeaveConversation", conversationId);
                    return;
                }

                joined = true;
            } catch (error) {
                console.error(`Failed to join conversation ${conversationId}`, error);
            }
        };

        void joinActiveConversation();

        return () => {
            disposed = true;

            if (joined && connection.state === "Connected") {
                void connection
                    .send("LeaveConversation", conversationId)
                    .catch((error) => {
                        console.error(`Failed to leave conversation ${conversationId}`, error);
                    });
            }
        };
    }, [connection, connectionStatus, connectionVersion, conversationId]);

    useEffect(() => {
        if (
            !connection ||
            connectionStatus !==
                "connected" ||
            !conversationId
            ) {
            return;
            }

        const fetchInitialMessages = async () => {
            try {
                setIsLoading(true);
                const token = currentUser ? await currentUser.getIdToken() : "";
                const initialMessages = await getMessagesForConversation(token, conversationId, CHAT_PAGE_SIZE);
                const normalizedMessages = initialMessages.map((msg) => ({
                    ...msg,
                    createDate: new Date(getUTCTime(msg.createDate)),
                }));
                normalizedMessages.sort((a, b) => new Date(a.createDate).getTime() - new Date(b.createDate).getTime());
                setMessages(previous => {
                    previous.forEach(message => {
                        if (message.isOptimistic && typeof message.attachmentUrl === "string" && message.attachmentUrl.startsWith("blob:")) {
                            URL.revokeObjectURL(message.attachmentUrl);
                        }
                    });
                    return normalizedMessages;
                });

                if (normalizedMessages.length > 0) {
                    earliestMessageIdRef.id = Math.min(...normalizedMessages.map(m => m.id));
                }
                setHasMore(normalizedMessages.length === CHAT_PAGE_SIZE);
            } catch (error) {
                console.error("Failed to load messages:", error);
                toast.error("Could not load chat history.");
            } finally {
                setIsLoading(false);
            }
        };

        fetchInitialMessages();

        // SignalR: Receive Message (Optimistic Replacement Logic)
        const handleReceiveMessage = (msg: Message) => {
            if (msg.conversationId !== conversationId) return;

            setMessages((prev) => {
                const isMyMessage = msg.author?.id === currentMyId;

                const existingServerMessage = prev.find(message => message.id === msg.id);
                if (existingServerMessage) {
                    return prev.flatMap(message => {
                        const isMatchingOptimistic = message.isOptimistic && Boolean(
                            msg.clientMessageId && message.clientMessageId === msg.clientMessageId
                        );
                        if (isMatchingOptimistic) {
                            if (typeof message.attachmentUrl === "string" && message.attachmentUrl.startsWith("blob:")) {
                                URL.revokeObjectURL(message.attachmentUrl);
                            }
                            pendingSendsRef.current.delete(message.id);
                            if (message.clientMessageId) void deleteOutboxItem(message.clientMessageId);
                            return [];
                        }
                        return [message.id === msg.id
                            ? { ...message, ...msg, createDate: new Date(msg.createDate), isOptimistic: false, deliveryStatus: "sent" as const }
                            : message];
                    });
                }

                // Handle Thread Reply
                if (msg.parentMessageId != null) {
                    return prev.map(m => {
                        if (m.id === msg.threadRootMessageId) {
                            return { ...m, threadReplyCount: (m.threadReplyCount || 0) + 1 };
                        }
                        return m;
                    });
                }

                let foundOptimisticMatch = false;
                const filtered = prev.filter(m => {
                    const clientIdMatches = Boolean(
                        msg.clientMessageId &&
                        m.clientMessageId &&
                        msg.clientMessageId === m.clientMessageId
                    );
                    const contentMatches = (m.messageContent || "").trim() === (msg.messageContent || "").trim();
                    const attachmentMatches = Boolean(m.attachmentUrl) === Boolean(msg.attachmentUrl);
                    if (m.isOptimistic && isMyMessage && (clientIdMatches || (contentMatches && attachmentMatches)) && !foundOptimisticMatch) {
                        foundOptimisticMatch = true;
                        if (typeof m.attachmentUrl === "string" && m.attachmentUrl.startsWith("blob:")) URL.revokeObjectURL(m.attachmentUrl);
                        pendingSendsRef.current.delete(m.id);
                        if (m.clientMessageId) {
                            activeDeliveriesRef.current.delete(m.clientMessageId);
                            void deleteOutboxItem(m.clientMessageId);
                        }
                        return false;
                    }
                    return true;
                });

                const normalizedMsg = { ...msg, createDate: new Date(msg.createDate), isNew: true };
                const updated = [...filtered, normalizedMsg].sort((a, b) => new Date(a.createDate).getTime() - new Date(b.createDate).getTime());

                return updated;
            });
        };

        const handleReceiveAttention = (msg: Message) => {
            if (
                msg.conversationId !== conversationId ||
                msg.author?.id === currentMyId ||
                msg.messageType === "CHANNEL_EVENT" ||
                msg.messageType === "SYSTEM" ||
                !isViewActiveRef.current
            ) return;

            receiveSound.play().catch((e) => console.error("Error playing sound:", e));
            chatMessagePreview$.next({ conversationId, message: msg });
            isNewMessageReceivedSubject.next(true);
        };

        // SignalR: Receive Reaction Update (INSIDE the useEffect)
        const handleReceiveReaction = (...args: any[]) => {
            console.group("🔵 INCOMING SIGNALR REACTION");
            console.log("All incoming arguments:", args);

            let msgId: number | null = null;
            let rUserId: string | null = null;
            let rType: string | null = null;
            let action: string | null = null;

            // Map the 5 arguments based on your C# backend signature
            if (args.length >= 5 && typeof args[0] === 'number' && typeof args[1] === 'number') {
                if (args[0] !== conversationId) {
                    console.groupEnd();
                    return;
                }
                msgId = args[1];     // e.g., 8826
                rUserId = args[2];   // e.g., '705HyjNk4...'
                rType = args[3];     // e.g., '😮' (will be null on 'removed')
                action = args[4];    // e.g., 'added' or 'removed'
            }

            console.log("Parsed Data:", { msgId, rUserId, rType, action });

            if (!msgId || !rUserId || !action) {
                console.warn("Could not extract required data from arguments.");
                console.groupEnd();
                return;
            }

            setMessages((prev: any) => {
                const targetMessage = prev.find((m: any) => m.id === msgId);
                if (!targetMessage) {
                    console.warn(`Message ${msgId} not found in local state.`);
                    console.groupEnd();
                    return prev;
                }

                return prev.map((m: any) => {
                    if (m.id !== msgId) return m;

                    const existingReactions = m.reactions || [];

                    if (action === 'removed') {
                        console.log(`Removing reaction for user: ${rUserId}`);
                        // If rType is null (as seen in your logs), we just remove the reaction matching the userId
                        return {
                            ...m,
                            reactions: existingReactions.filter((r: any) => {
                                if (rType) return !(r.userId === rUserId && r.reactionType === rType);
                                return r.userId !== rUserId;
                            })
                        };
                    }

                    if ((action === 'added' || action === 'updated') && rType) {
                        console.log(`Adding reaction ${rType} for user: ${rUserId}`);
                        // Check if it already exists (to prevent duplicating our own optimistic update)
                        const previousReaction = existingReactions.find((r: any) => r.userId === rUserId);
                        const exists = previousReaction?.reactionType === rType;
                        if (exists) return m;

                        const isMe = rUserId === currentMyId;

                        return {
                            ...m,
                            reactions: [...existingReactions.filter((r: any) => r.userId !== rUserId), {
                                ...previousReaction,
                                id: Date.now(), // Temp ID for React key
                                userId: rUserId,
                                fullName: isMe ? "You" : previousReaction?.fullName || "Someone", // Fallback for other users
                                reactionType: rType
                            }]
                        };
                    }

                    return m;
                });
            });
            console.groupEnd();
        };

        const handleTypingStatus = (
            typingConversationId: number,
            typingUserId: string,
            currentlyTyping: boolean
            ) => {
            if (
                typingConversationId !==
                conversationId ||
                !typingUserId ||
                typingUserId === currentMyId
            ) {
                return;
            }

            setTypingUsers((previous) => {
                if (currentlyTyping) {
                return previous.includes(
                    typingUserId
                )
                    ? previous
                    : [
                        ...previous,
                        typingUserId,
                    ];
                }

                return previous.filter(
                (userId) =>
                    userId !== typingUserId
                );
            });
        };

        const handleBookmarkUpdate = (update: { messageId?: number; MessageId?: number; isBookmarked?: boolean; IsBookmarked?: boolean }) => {
            const messageId = update.messageId ?? update.MessageId;
            if (!messageId) return;
            const isBookmarked = update.isBookmarked ?? update.IsBookmarked ?? false;
            setMessages(previous => previous.map(message => message.id === messageId ? { ...message, isBookmarked } : message));
        };

        const handleMessageUpdate = (update: any) => {
            if (update.conversationId !== conversationId) return;
            setMessages(previous => previous.map(message => message.id === update.messageId ? {
                ...message,
                messageContent: update.newContent,
                messageDoc: update.messageDoc,
                messagePlaintext: update.messagePlaintext ?? update.newContent,
                isEdited: true,
            } : message));
        };

        const handleMessageDelete = (update: any) => {
            if (update.conversationId !== conversationId) return;
            setMessages(previous => previous.map(message => message.id === update.messageId ? { ...message, isDeleted: true } : message));
        };

        connection.on(
        "ReceiveTypingStatusForUser",
        handleTypingStatus
        );

        connection.on(
        "ReceiveTypingStatus",
        handleTypingStatus
        );
        connection.on("ReceiveReaction", handleReceiveReaction);
        connection.on("ReceiveReactionUpdate", handleReceiveReaction);
        connection.on("ReceiveBookmarkUpdate", handleBookmarkUpdate);


        connection.on("ReceiveMessage", handleReceiveMessage);
        connection.on("ReceiveChatAttention", handleReceiveAttention);
        connection.on("ReceiveMessageUpdate", handleMessageUpdate);
        connection.on("ReceiveMessageDelete", handleMessageDelete);

        return () => {
            connection.off("ReceiveMessage", handleReceiveMessage);
            connection.off("ReceiveChatAttention", handleReceiveAttention);
            connection.off("ReceiveReaction", handleReceiveReaction);
            connection.off("ReceiveReactionUpdate", handleReceiveReaction);
            connection.off("ReceiveBookmarkUpdate", handleBookmarkUpdate);
            connection.off("ReceiveMessageUpdate", handleMessageUpdate);
            connection.off("ReceiveMessageDelete", handleMessageDelete);
            connection.off(
                "ReceiveTypingStatusForUser",
                handleTypingStatus
                );

                connection.off(
                "ReceiveTypingStatus",
                handleTypingStatus
                );
        };
    }, [  connection,
  connectionStatus,
  connectionVersion,
  conversationId,
  currentUser,
  currentMyId,
  receiveSound,
  earliestMessageIdRef,]);

    const loadMoreMessages = useCallback(async (pageSize = 50): Promise<number> => {
        if (!conversationId || loadingMore || !hasMore || !currentUser || earliestMessageIdRef.id === null) return 0;
        try {
            setLoadingMore(true);
            const token = await currentUser.getIdToken();
            const olderMessages = await getMessagesForConversation(token, conversationId, pageSize, earliestMessageIdRef.id || undefined);
            if (!olderMessages || olderMessages.length === 0) {
                setHasMore(false);
                return 0;
            }
            const normalized = olderMessages.map((msg) => ({ ...msg, createDate: new Date(getUTCTime(msg.createDate)) }));
            normalized.sort((a, b) => new Date(a.createDate).getTime() - new Date(b.createDate).getTime());
            setMessages((prev) => {
                const newList = [...normalized, ...prev];
                earliestMessageIdRef.id = Math.min(...newList.map(m => m.id));
                return newList;
            });
            setHasMore(olderMessages.length === pageSize);
            return normalized.length;
        } catch (err) {
            console.error("Failed to load older messages:", err);
            return 0;
        } finally {
            setLoadingMore(false);
        }
    }, [conversationId, currentUser, earliestMessageIdRef, loadingMore, hasMore]);

    const markAsRead = useCallback(() => {
        if (connection?.state === "Connected") {
            connection.invoke("MarkAsRead", conversationId).catch(console.error);
            conversationRead$.next(conversationId);
        }
    }, [connection, conversationId]);

    const acknowledgePendingMessage = async (tempId: number, pending: PendingChatOutboxItem, accepted: Message) => {
        activeDeliveriesRef.current.delete(pending.clientMessageId);
        pendingSendsRef.current.delete(tempId);
        await deleteOutboxItem(pending.clientMessageId);

        setMessages(previous => {
            const alreadyAccepted = previous.some(message => message.id === accepted.id);
            return previous.flatMap(message => {
                    if (message.id === accepted.id && message.id !== tempId) {
                        return [{ ...message, ...accepted, createDate: new Date(accepted.createDate), deliveryStatus: "sent" as const }];
                    }
                    if (message.id !== tempId) return [message];
                    if (typeof message.attachmentUrl === "string" && message.attachmentUrl.startsWith("blob:")) {
                        URL.revokeObjectURL(message.attachmentUrl);
                    }
                    if (alreadyAccepted) return [];
                    return [{
                        ...message,
                        ...accepted,
                        id: accepted.id,
                        clientMessageId: pending.clientMessageId,
                        createDate: new Date(accepted.createDate),
                        attachmentUrl: accepted.attachmentUrl || message.attachmentUrl,
                        isOptimistic: false,
                        deliveryStatus: "sent" as const,
                    }];
                });
        });
    };

    const deliverPendingMessage = async (tempId: number, pending: PendingChatOutboxItem) => {
        if (!pendingSendsRef.current.has(tempId)) return;
        if (activeDeliveriesRef.current.has(pending.clientMessageId)) return;
        activeDeliveriesRef.current.add(pending.clientMessageId);
        let lastError: unknown;
        const maximumAttempts = 3;
        for (let attempt = pending.attemptCount; attempt < maximumAttempts; attempt += 1) {
            const deliveryStatus: ChatDeliveryStatus = attempt === 0 ? "sending" : "retrying";
            pending.attemptCount = attempt + 1;
            pending.status = deliveryStatus;
            void updateOutboxItem(pending.clientMessageId, {
                attemptCount: pending.attemptCount,
                status: deliveryStatus,
                lastError: undefined,
            }).catch(error => console.warn("Unable to persist message delivery state:", error));
            setMessages(previous => previous.map(message => message.id === tempId
                ? { ...message, deliveryStatus }
                : message));

            try {
                let token = "";
                if (pending.file && !pending.attachmentUrl) {
                    token = currentUser ? await currentUser.getIdToken() : "";
                    const formData = new FormData();
                    formData.append("file", pending.file);
                    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/chat/upload-attachment`, {
                        method: "POST",
                        headers: GetHeaders(token, false),
                        body: formData,
                    });
                    if (!response.ok) throw new Error(await response.text() || "File upload failed.");
                    const data = await response.json();
                    pending.attachmentUrl = data.documentUrl;
                    pending.attachmentOriginalName = data.originalName;
                    pending.attachmentContentType = data.contentType;
                    pending.attachmentSize = data.size;
                    pending.attachmentScanStatus = data.scanStatus;
                    await updateOutboxItem(pending.clientMessageId, {
                        attachmentUrl: pending.attachmentUrl,
                        attachmentOriginalName: pending.attachmentOriginalName,
                        attachmentContentType: pending.attachmentContentType,
                        attachmentSize: pending.attachmentSize,
                        attachmentScanStatus: pending.attachmentScanStatus,
                    });
                }

                if (currentUser) {
                    let accepted: Message;
                    if (connection?.state === "Connected") {
                        try {
                            accepted = await connection.invoke<Message>(
                                "SendMessage",
                                pending.conversationId,
                                pending.text,
                                pending.doc,
                                pending.parentMessageId,
                                pending.clientMessageId,
                                pending.attachmentUrl,
                            );
                        } catch (signalRError) {
                            // The same clientMessageId makes this fallback safe even if
                            // the hub committed just before the connection was interrupted.
                            console.warn("SignalR send failed; retrying through REST.", signalRError);
                            token ||= await currentUser.getIdToken();
                            accepted = await sendMessageApi(
                                token,
                                pending.conversationId,
                                pending.text,
                                pending.doc,
                                pending.attachmentUrl,
                                pending.parentMessageId,
                                pending.clientMessageId,
                            );
                        }
                    } else {
                        token ||= await currentUser.getIdToken();
                        accepted = await sendMessageApi(
                            token,
                            pending.conversationId,
                            pending.text,
                            pending.doc,
                            pending.attachmentUrl,
                            pending.parentMessageId,
                            pending.clientMessageId,
                        );
                    }
                    await acknowledgePendingMessage(tempId, pending, accepted);
                } else {
                    // External chat sessions authenticate through SignalR rather
                    // than the Firebase-protected REST endpoint.
                    if (pending.attachmentUrl) throw new Error("Attachments require an authenticated chat session.");
                    if (!connection || connection.state !== "Connected") throw new Error("Chat is reconnecting.");
                    const accepted = await connection.invoke<Message>(
                        "SendMessage",
                        pending.conversationId,
                        pending.text.trim(),
                        pending.doc,
                        pending.parentMessageId,
                        pending.clientMessageId,
                    );
                    await acknowledgePendingMessage(tempId, pending, accepted);
                }
                return;
            } catch (error) {
                lastError = error;
                const message = error instanceof Error ? error.message : "Message delivery failed.";
                const permissionFailure = /403|forbidden|read-only|archived/i.test(message);
                if (permissionFailure || attempt >= maximumAttempts - 1) break;
                await new Promise(resolve => window.setTimeout(resolve, 500 * (2 ** attempt)));
            }
        }

        activeDeliveriesRef.current.delete(pending.clientMessageId);
        const errorMessage = lastError instanceof Error ? lastError.message : "Message delivery failed.";
        pending.status = "failed";
        pending.lastError = errorMessage;
        await updateOutboxItem(pending.clientMessageId, { status: "failed", lastError: errorMessage });
        console.error("Failed to send queued message:", lastError);
        setMessages(previous => previous.map(message => message.id === tempId
            ? { ...message, deliveryStatus: "failed" as const }
            : message));
        toast.error("Message could not be sent. Select Retry to try again.");
    };

    const enqueuePendingMessage = (tempId: number, pending: PendingChatOutboxItem) => {
        if (scheduledDeliveriesRef.current.has(pending.clientMessageId)) return;
        scheduledDeliveriesRef.current.add(pending.clientMessageId);
        const currentQueue = sendQueuesRef.current.get(pending.conversationId) ?? Promise.resolve();
        const nextQueue = currentQueue
            .catch(() => undefined)
            .then(() => deliverPendingMessage(tempId, pending))
            .finally(() => scheduledDeliveriesRef.current.delete(pending.clientMessageId));
        sendQueuesRef.current.set(pending.conversationId, nextQueue);
    };

    const sendMessage = async (text: string, doc?: any, file?: File, parentMessageId?: number) => {
        if (!text.trim() && !file) return;

        const clientMessageId = createClientMessageId();
        const tempId = createOptimisticTempId(clientMessageId);

        // FIX: Fully populate optimistic author to satisfy UserInfo interface
        const optimisticAuthor = {
            id: currentMyId || "",
            firstName: currentUser?.displayName?.split(' ')[0] || "Me",
            lastName: currentUser?.displayName?.split(' ')[1] || "",
            displayName: currentUser?.displayName || "Me",
            email: currentUser?.email || "",
            scid: null,
            joinDate: new Date(),
            organization: {} as any,
            roles: [],
            regions: [],
        } as UserInfo;

        let optimisticAttachmentType = "";
        let optimisticAttachmentUrl: string | null = null;

        if (file) {
            const extension = file.name.split('.').pop()?.toLowerCase();
            optimisticAttachmentType = ['jpg', 'jpeg', 'png', 'gif'].includes(extension || '') ? 'image' : 'other';
            optimisticAttachmentUrl = URL.createObjectURL(file);
        }

        const optimisticMsg: OptimisticMessage = {
            id: tempId,
            conversationId: conversationId,
            author: optimisticAuthor,
            messageContent: text,
            messageDoc: doc,
            createDate: new Date(),
            messageType: "USER",
            isOptimistic: true,
            deliveryStatus: "queued",
            clientMessageId,
            clientOrder: Math.abs(tempId),
            optimisticAttachmentName: file?.name,
            reactions: [],
            isDeleted: false,
            isEdited: false,
            threadReplyCount: 0,
            attachmentType: optimisticAttachmentType,
            attachmentOriginalName: file?.name,
            attachmentContentType: file?.type,
            attachmentSize: file?.size,
            attachmentScanStatus: file ? "pending" : undefined,
            attachmentUrl: optimisticAttachmentUrl,
            parentMessageId,
            threadRootMessageId: parentMessageId,
            lastReadAt: new Date().getTime() 
        };

        const pending: PendingChatOutboxItem = {
            clientMessageId,
            tempId,
            userId: currentMyId || "",
            conversationId,
            parentMessageId,
            text,
            doc,
            file,
            attachmentType: optimisticAttachmentType,
            attachmentOriginalName: file?.name,
            attachmentContentType: file?.type,
            attachmentSize: file?.size,
            attachmentScanStatus: file ? "pending" : undefined,
            createdAt: Date.now(),
            attemptCount: 0,
            status: "queued",
        };
        pendingSendsRef.current.set(tempId, pending);
        setMessages(prev => [...prev, optimisticMsg]);
        void (async () => {
            try {
                // Durability is established before network delivery, while the UI is
                // already updated synchronously for immediate feedback.
                await saveOutboxItem(pending);
            } catch (error) {
                console.error("Unable to persist the queued chat message:", error);
                toast.warn("This message is queued, but it may not survive a page refresh.");
            }
            enqueuePendingMessage(tempId, pending);
        })();
    };

    const retryMessage = (tempId: number) => {
        const pending = pendingSendsRef.current.get(tempId);
        if (!pending) return;
        pending.attemptCount = 0;
        pending.status = "queued";
        pending.lastError = undefined;
        void updateOutboxItem(pending.clientMessageId, { attemptCount: 0, status: "queued", lastError: undefined });
        setMessages(previous => previous.map(message => message.id === tempId
            ? { ...message, deliveryStatus: "queued" as const }
            : message));
        enqueuePendingMessage(tempId, pending);
    };

    const cancelMessage = async (tempId: number) => {
        const pending = pendingSendsRef.current.get(tempId);
        if (!pending || activeDeliveriesRef.current.has(pending.clientMessageId)) return;
        pendingSendsRef.current.delete(tempId);
        await deleteOutboxItem(pending.clientMessageId);
        setMessages(previous => previous.filter(message => {
            if (message.id !== tempId) return true;
            if (typeof message.attachmentUrl === "string" && message.attachmentUrl.startsWith("blob:")) {
                URL.revokeObjectURL(message.attachmentUrl);
            }
            return false;
        }));
    };

    useEffect(() => {
        if (!currentMyId || !conversationId || isLoading) return;
        let cancelled = false;

        const restoreOutbox = async () => {
            try {
                const storedItems = await getConversationOutbox(currentMyId, conversationId);
                if (cancelled) return;

                for (const storedItem of storedItems) {
                    if (storedItem.status === "sent") {
                        await deleteOutboxItem(storedItem.clientMessageId);
                        continue;
                    }

                    pendingSendsRef.current.set(storedItem.tempId, storedItem);

                    const optimisticAuthor = {
                        id: currentMyId,
                        firstName: currentUser?.displayName?.split(" ")[0] || "Me",
                        lastName: currentUser?.displayName?.split(" ")[1] || "",
                        displayName: currentUser?.displayName || "Me",
                        email: currentUser?.email || "",
                        scid: null,
                        joinDate: new Date(),
                        organization: {} as any,
                        roles: [],
                        regions: [],
                    } as UserInfo;

                    setMessages(previous => {
                        if (previous.some(message => message.clientMessageId === storedItem.clientMessageId)) return previous;
                        const optimisticAttachmentUrl = storedItem.file ? URL.createObjectURL(storedItem.file) : null;
                        return [...previous, {
                                id: storedItem.tempId,
                                clientMessageId: storedItem.clientMessageId,
                                conversationId: storedItem.conversationId,
                                author: optimisticAuthor,
                                messageContent: storedItem.text,
                                messageDoc: storedItem.doc,
                                createDate: new Date(storedItem.createdAt),
                                messageType: "USER",
                                isOptimistic: true,
                                deliveryStatus: storedItem.status === "failed" ? "failed" : "queued",
                                clientOrder: storedItem.createdAt,
                                optimisticAttachmentName: storedItem.file?.name,
                                reactions: [],
                                isDeleted: false,
                                isEdited: false,
                                threadReplyCount: 0,
                                attachmentType: storedItem.attachmentType || "",
                                attachmentOriginalName: storedItem.attachmentOriginalName ?? storedItem.file?.name,
                                attachmentContentType: storedItem.attachmentContentType ?? storedItem.file?.type,
                                attachmentSize: storedItem.attachmentSize ?? storedItem.file?.size,
                                attachmentScanStatus: storedItem.attachmentScanStatus,
                                attachmentUrl: optimisticAttachmentUrl,
                                parentMessageId: storedItem.parentMessageId,
                                threadRootMessageId: storedItem.parentMessageId,
                                lastReadAt: storedItem.createdAt,
                            }];
                    });

                    if (storedItem.status !== "failed" && !activeDeliveriesRef.current.has(storedItem.clientMessageId)) {
                        storedItem.status = "queued";
                        await updateOutboxItem(storedItem.clientMessageId, { status: "queued" });
                        enqueuePendingMessage(storedItem.tempId, storedItem);
                    }
                }
            } catch (error) {
                console.error("Unable to restore the chat outbox:", error);
            }
        };

        void restoreOutbox();
        return () => { cancelled = true; };
        // connectionVersion intentionally retries persisted queued sends after a reconnect.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [connectionVersion, conversationId, currentMyId, currentUser, isLoading]);

    return {
        messages,
        setMessages,
        isLoading,
        typingUsers,
        sendMessage,
        retryMessage,
        cancelMessage,
        loadMoreMessages,
        hasMore,
        loadingMore,
        markAsRead,
        sendTypingStatus: (isTyping: boolean) => {
            if (
                !connection ||
                connectionStatus !== "connected" ||
                !conversationId ||
                typingStateRef.isTyping === isTyping
            ) {
                return;
            }

            typingStateRef.isTyping = isTyping;

            // Typing is transient and does not need a completion result. Using
            // send avoids a type-3 completion frame for every status change.
            connection.send("SendTypingStatus", conversationId, isTyping).catch((error) => {
                typingStateRef.isTyping = !isTyping;
                console.error("Failed to send typing status", error);
            });
        },
        sendReaction: (msgId: number, type: string) => {
            const currentUserId = currentMyId || "";
            const firstName = currentUser?.displayName?.split(' ')[0] || "You";
            const lastName = currentUser?.displayName?.split(' ')[1] || "";
            const fullName = `${firstName} ${lastName}`.trim();
            const targetMessage = messages.find(message => message.id === msgId);
            if (!targetMessage || !currentUserId || !connection) return;
            const previousReactions = targetMessage.reactions || [];
            const hasReacted = previousReactions.some(reaction => reaction.userId === currentUserId && reaction.reactionType === type);
            const mutationKey = `${msgId}:${currentUserId}`;
            const mutationVersion = (reactionMutationVersionsRef.current.get(mutationKey) ?? 0) + 1;
            reactionMutationVersionsRef.current.set(mutationKey, mutationVersion);

            setMessages((prev) => {
                return prev.map(msg => {
                    if (msg.id !== msgId) return msg;

                    const existingReactions = msg.reactions || [];
                    if (hasReacted) {
                        return { ...msg, reactions: existingReactions.filter(reaction => !(reaction.userId === currentUserId && reaction.reactionType === type)) };
                    }
                    return {
                        ...msg,
                        reactions: [...existingReactions.filter(reaction => reaction.userId !== currentUserId), {
                            id: Date.now(),
                            userId: currentUserId,
                            fullName: fullName,
                            reactionType: type
                        }],
                    };
                });
            });

            connection.invoke("SendReaction", msgId, type).catch(err => {
                console.error("Failed to send reaction via SignalR:", err);
                if (reactionMutationVersionsRef.current.get(mutationKey) !== mutationVersion) return;
                setMessages(previous => previous.map(message => message.id === msgId
                    ? { ...message, reactions: previousReactions }
                    : message));
                toast.error("Reaction could not be updated.");
            });
        }
    };
};
